import { create } from 'zustand'

const STORAGE_KEY = 'shadowtooti.theme'
const MODES = ['light', 'dark', 'auto']

/** design-system.md: auto = dark between 19:00 and 07:00 device time. */
function autoIsDark(now = new Date()) {
  const hour = now.getHours()
  return hour >= 19 || hour < 7
}

function resolve(mode) {
  return mode === 'auto' ? (autoIsDark() ? 'dark' : 'light') : mode
}

const THEME_COLOR = { light: '#f4efe6', dark: '#0a1017' }

function apply(mode) {
  const resolved = resolve(mode)
  document.documentElement.dataset.theme = resolved
  // The two static <meta name="theme-color" media="..."> tags in
  // index.html only ever follow the OS setting; this keeps the browser
  // chrome/status-bar tint in sync with what the learner actually chose
  // (or with the 19:00-07:00 auto rule), which can disagree with the OS.
  document
    .querySelectorAll('meta[name="theme-color"]')
    .forEach((tag) => tag.setAttribute('content', THEME_COLOR[resolved]))
}

function loadStoredMode() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return MODES.includes(stored) ? stored : 'auto'
  } catch {
    // No storage (a private window, the unit environment): the default, and nothing remembered.
    return 'auto'
  }
}

const initialMode = loadStoredMode()
if (typeof document !== 'undefined') apply(initialMode)

export const useThemeStore = create((set, get) => ({
  mode: initialMode,

  setMode(mode) {
    if (!MODES.includes(mode)) return
    try {
      localStorage.setItem(STORAGE_KEY, mode)
    } catch {
      // Storage refused: the theme still applies for this session.
    }
    apply(mode)
    set({ mode })
  },

  /** Re-check the clock — call this on an interval while `mode === 'auto'`
   *  so a session left open across the 19:00/07:00 boundary still flips. */
  recomputeIfAuto() {
    if (get().mode === 'auto') apply('auto')
  },
}))

/** The mode as it currently paints — the site header toggles between light and dark from here rather than through "auto". */
export function resolveMode(mode) {
  return resolve(mode)
}
